import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface SpeechPhase {
  name: string;
  duration: string;
  purpose: string;
  icon: string;
}

interface SpeakingSkill {
  name: string;
  weight: string;
  tip: string;
  icon: string;
  color: string;
}

interface FamousSpeaker {
  name: string;
  quote: string;
  era: string;
  country: string;
}

interface EngagementAxis {
  label: string;
  value: number;
}

interface PublicSpeakingTemplateProps {
  topic: string;
  subtitle: string;
  speechPhases: SpeechPhase[];
  skills: SpeakingSkill[];
  speakers: FamousSpeaker[];
  engagementAxes: EngagementAxis[];
  bodyLanguageRules: { label: string; icon: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: PublicSpeakingTemplateProps = {
  topic: '演讲与口才艺术',
  subtitle: 'Public Speaking & Rhetoric',
  speechPhases: [
    { name: '开场白', duration: '10-15%', purpose: '吸引注意力，建立连接', icon: '🎬' },
    { name: '论点展开', duration: '60-70%', purpose: '逻辑递进，案例支撑', icon: '📊' },
    { name: '高潮升华', duration: '10-15%', purpose: '情感共鸣，核心金句', icon: '🔥' },
    { name: '有力收尾', duration: '5-10%', purpose: '行动号召，余韵留香', icon: '🎯' },
  ],
  skills: [
    {
      name: '眼神交流',
      weight: '关键',
      tip: '扫视全场，3-5秒锁定一人',
      icon: '👁️',
      color: '#f97316',
    },
    {
      name: '肢体语言',
      weight: '55%',
      tip: '开放手势，稳定站姿',
      icon: '🤲',
      color: '#fb923c',
    },
    {
      name: '声音控制',
      weight: '38%',
      tip: '语速变化，战略性停顿',
      icon: '🎙️',
      color: '#fdba74',
    },
    {
      name: '即兴应变',
      weight: '高阶',
      tip: '框架思维，桥接技巧',
      icon: '⚡',
      color: '#fbbf24',
    },
  ],
  speakers: [
    { name: '马丁·路德·金', quote: 'I Have a Dream', era: '1963', country: '🇺🇸' },
    { name: '丘吉尔', quote: 'We Shall Never Surrender', era: '1940', country: '🇬🇧' },
    { name: '史蒂夫·乔布斯', quote: 'Stay Hungry, Stay Foolish', era: '2005', country: '🇺🇸' },
    { name: '闻一多', quote: '最后一次演讲', era: '1946', country: '🇨🇳' },
  ],
  engagementAxes: [
    { label: '逻辑性', value: 0.85 },
    { label: '情感力', value: 0.9 },
    { label: '故事性', value: 0.8 },
    { label: '互动性', value: 0.75 },
    { label: '视觉辅助', value: 0.7 },
    { label: '幽默感', value: 0.65 },
  ],
  bodyLanguageRules: [
    { label: '权力姿势', icon: '💪' },
    { label: '手势映射', icon: '✋' },
    { label: '舞台移动', icon: '🚶' },
    { label: '面部表情', icon: '😊' },
  ],
  stats: [
    { value: '18min', label: 'TED演讲平均时长' },
    { value: '7/38/55', label: '文字/声音/肢体占比' },
    { value: '72h', label: '听众遗忘90%内容' },
  ],
  bottomQuote:
    '一次好的演讲不是让听众记住你说了什么，而是让他们记住自己感受到了什么 —— 玛雅·安杰卢',
};

export const PublicSpeakingTemplate: React.FC<
  Partial<PublicSpeakingTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const phaseStart = 30;
  const skillStart = 150;
  const radarStart = 200;
  const speakerStart = 280;
  const bodyLangStart = 370;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const radarDraw = interpolate(
    frame,
    [radarStart, radarStart + 80],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const radarCx = 195;
  const radarCy = 210;
  const radarR = 155;

  const getRadarPoint = (index: number, value: number) => {
    const angle = (Math.PI * 2 * index) / p.engagementAxes.length - Math.PI / 2;
    const r = radarR * value * radarDraw;
    return {
      x: radarCx + r * Math.cos(angle),
      y: radarCy + r * Math.sin(angle),
    };
  };

  const radarPath = p.engagementAxes
    .map((axis, i) => {
      const pt = getRadarPoint(i, axis.value);
      return `${i === 0 ? 'M' : 'L'}${pt.x},${pt.y}`;
    })
    .join(' ') + ' Z';

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1c1008 0%, #2a1206 40%, #1a0a02 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating microphone-like particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 311) % 1920;
        const y = (i * 179) % 1080;
        const size = 3 + (i % 4);
        const drift = Math.sin(frame * 0.025 + i) * 10;
        const pulse = 0.06 + 0.05 * Math.sin(frame * 0.04 + i * 0.8);
        const colors = ['#f97316', '#fb923c', '#fbbf24', '#fdba74'];
        const isMic = i % 6 === 0;
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: isMic ? size * 1.2 : size,
              height: isMic ? size * 2 : size,
              borderRadius: isMic ? '40% 40% 50% 50%' : '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 ${size * 3}px ${colors[i % colors.length]}40`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#c2410c',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#fed7aa',
            flexShrink: 0,
          }}
        >
          🎤 演讲技巧
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#fed7aa',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#a8a29e',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Speech Structure ===== */}
      <div style={{ position: 'absolute', top: 130, left: 60, width: 570 }}>
        <div
          style={{
            fontSize: 26,
            color: '#fed7aa',
            marginBottom: 14,
            letterSpacing: '0.08em',
            fontWeight: 700,
          }}
        >
          演讲结构
        </div>
        <svg width="460" height="620" viewBox="0 0 460 620">
          <defs>
            <linearGradient id="barGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#f97316" stopOpacity="0.08" />
            </linearGradient>
            <filter id="barGlow">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {p.speechPhases.map((phase, i) => {
            const yPos = i * 150;
            const s = spring({
              frame: frame - (phaseStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const barWidth = interpolate(
              parseFloat(phase.duration),
              [5, 70],
              [80, 380],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <g key={i} opacity={s}>
                <rect
                  x="10"
                  y={yPos + 8}
                  width={barWidth * s}
                  height="65"
                  rx="12"
                  fill="url(#barGrad)"
                  stroke="#c2410c60"
                  strokeWidth="1.5"
                  filter="url(#barGlow)"
                />
                <circle
                  cx="36"
                  cy={yPos + 40}
                  r="20"
                  fill="#c2410c30"
                  stroke="#f9731650"
                  strokeWidth="1.5"
                />
                <text
                  x="36"
                  y={yPos + 46}
                  fill="#fed7aa"
                  fontSize="20"
                  textAnchor="middle"
                >
                  {phase.icon}
                </text>
                <text
                  x="66"
                  y={yPos + 36}
                  fill="#fed7aa"
                  fontSize="22"
                  fontWeight="800"
                >
                  {phase.name}
                </text>
                <text x="66" y={yPos + 60} fill="#fdba74" fontSize="20" fontWeight="600">
                  {phase.duration}
                </text>
                <text x="18" y={yPos + 96} fill="#a8a29e" fontSize="20">
                  {phase.purpose}
                </text>
                {i < p.speechPhases.length - 1 && (
                  <line
                    x1="36"
                    y1={yPos + 108}
                    x2="36"
                    y2={yPos + 148}
                    stroke="#c2410c"
                    strokeWidth="2.5"
                    strokeDasharray="5 4"
                    opacity={0.5}
                  />
                )}
                {i < p.speechPhases.length - 1 && (
                  <polygon
                    points={`30,${yPos + 143} 42,${yPos + 143} 36,${yPos + 152}`}
                    fill="#f97316"
                    opacity={0.7}
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* ===== CENTER: Skills + Radar ===== */}
      <div style={{ position: 'absolute', top: 130, left: 550, width: 700 }}>
        <div
          style={{
            fontSize: 26,
            color: '#a8a29e',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          核心技能
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {p.skills.map((sk, i) => {
            const s = spring({
              frame: frame - (skillStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: '#1c1008',
                  borderRadius: 14,
                  padding: '10px 14px',
                  border: `1.5px solid ${sk.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: `${sk.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 26,
                    flexShrink: 0,
                    boxShadow: `0 0 18px ${sk.color}30`,
                  }}
                >
                  {sk.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: sk.color }}
                    >
                      {sk.name}
                    </span>
                    <span style={{ fontSize: 20, color: '#78716c' }}>
                      {sk.weight}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 20, color: '#a8a29e', marginTop: 2 }}
                  >
                    {sk.tip}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Audience Engagement Radar */}
        <div style={{ marginTop: 24 }}>
          <div
            style={{
            fontSize: 26,
            color: '#a8a29e',
            marginBottom: 10,
            letterSpacing: '0.08em',
            fontWeight: 700,
          }}
        >
          听众互动雷达图
        </div>
          <svg width="700" height="430" viewBox="0 0 700 430">
            {[0.25, 0.5, 0.75, 1].map((scale, si) => {
              const pts = p.engagementAxes
                .map((_, i) => {
                  const angle =
                    (Math.PI * 2 * i) / p.engagementAxes.length - Math.PI / 2;
                  const r = radarR * scale;
                  return `${radarCx + r * Math.cos(angle)},${radarCy + r * Math.sin(angle)}`;
                })
                .join(' ');
              return (
                <polygon
                  key={si}
                  points={pts}
                  fill="none"
                  stroke="#c2410c"
                  strokeWidth="1"
                  opacity={0.2}
                />
              );
            })}
            {p.engagementAxes.map((axis, i) => {
              const angle =
                (Math.PI * 2 * i) / p.engagementAxes.length - Math.PI / 2;
              const lx = radarCx + (radarR + 35) * Math.cos(angle);
              const ly = radarCy + (radarR + 35) * Math.sin(angle);
              return (
                <g key={i}>
                  <line
                    x1={radarCx}
                    y1={radarCy}
                    x2={radarCx + radarR * Math.cos(angle)}
                    y2={radarCy + radarR * Math.sin(angle)}
                    stroke="#c2410c"
                    strokeWidth="1"
                    opacity={0.3}
                  />
                  <text
                    x={lx}
                    y={ly}
                    fill="#a8a29e"
                    fontSize="22"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {axis.label}
                  </text>
                </g>
              );
            })}
            <polygon
              points={p.engagementAxes
                .map((_, i) => {
                  const pt = getRadarPoint(i, p.engagementAxes[i].value);
                  return `${pt.x},${pt.y}`;
                })
                .join(' ')}
              fill="#f9731640"
              stroke="#f97316"
              strokeWidth="3"
              opacity={radarDraw}
            />
            {p.engagementAxes.map((axis, i) => {
              const pt = getRadarPoint(i, axis.value);
              return (
                <circle
                  key={`dot-${i}`}
                  cx={pt.x}
                  cy={pt.y}
                  r="6"
                  fill="#f97316"
                  opacity={radarDraw}
                />
              );
            })}

            {/* Mehrabian Rule visualization */}
            <g
              opacity={interpolate(
                frame,
                [radarStart + 60, radarStart + 90],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              )}
            >
              <text x="440" y="50" fill="#fed7aa" fontSize="22" fontWeight="700">
                梅拉比安法则
              </text>
              {[
                { label: '肢体语言 55%', w: 330, color: '#f97316' },
                { label: '声音语调 38%', w: 240, color: '#fb923c' },
                { label: '文字内容 7%', w: 140, color: '#fdba74' },
              ].map((bar, i) => (
                <g key={`bar-${i}`}>
                  <rect
                    x="440"
                    y={80 + i * 48}
                    width={bar.w}
                    height="32"
                    rx="8"
                    fill={bar.color}
                    opacity={0.8}
                  />
                  <text
                    x="450"
                    y={102 + i * 48}
                    fill="#1c1008"
                    fontSize="20"
                    fontWeight="700"
                  >
                    {bar.label}
                  </text>
                </g>
              ))}
            </g>
          </svg>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Famous Speakers + Body Language ===== */}
      <div style={{ position: 'absolute', top: 130, right: 50, width: 500 }}>        
        <div
          style={{
            fontSize: 26,
            color: '#a8a29e',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          经典演讲
        </div>
        {p.speakers.map((sp, i) => {
          const s = spring({
            frame: frame - (speakerStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#1c1008',
                borderRadius: 12,
                padding: '12px 16px',
                border: '1.5px solid #44280f',
                marginBottom: 8,
                opacity: s,
                transform: `translateY(${(1 - s) * 16}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{sp.country}</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: '#fdba74' }}>
                  {sp.name}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                <span style={{ fontSize: 22, color: '#f97316', fontWeight: 700 }}>
                  "{sp.quote}"
                </span>
                <span style={{ fontSize: 2, color: '#78716c' }}>{sp.era}年</span>
              </div>
            </div>
          );
        })}

        {/* Body Language Rules */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 24,
              color: '#a8a29e',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            肢体语言要素
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.bodyLanguageRules.map((rule, i) => {
              const s = spring({
                frame: frame - (bodyLangStart + i * 12),
                fps,
                config: { damping: 14, stiffness: 130 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#1c1008',
                    borderRadius: 12,
                    padding: '14px 10px',
                    textAlign: 'center',
                    border: '1px solid #44280f',
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ fontSize: 26, marginBottom: 4 }}>{rule.icon}</div>
                  <div
                    style={{ fontSize: 22, fontWeight: 700, color: '#fdba74' }}
                  >
                    {rule.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1c1008',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #44280f',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{ fontSize: 30, fontWeight: 900, color: '#f97316' }}
              >
                {st.value}
              </div>
              <div style={{ fontSize: 20, color: '#a8a29e', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#1c1008',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #c2410c',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🎤</span>
          <span style={{ fontSize: 22, color: '#fed7aa', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #c2410c, #f97316, #fbbf24)',
        }}
      />
    </AbsoluteFill>
  );
};
